import { Eye, History, LeafIcon, Edit, Trash2, UploadCloudIcon, ChevronLeft, ChevronRight, Receipt, HandCoins, Rewind } from "lucide-react";
import { ContainerCard, ModuleTitleCard, EmployeeDetails } from "../../components/ui/cards";
import { useCallback, useEffect, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { ADVANCE_URL } from "../../constants/api";
import { truncateText } from "../../components/common/CommonFunctions";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { DatePicker, Select, Upload as AntUpload, Image, TimePicker } from "antd";
import dayjs from 'dayjs';
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";

const TERMINAL_ADVANCE_CANCEL_STATUSES = new Set([
    'rejected',
    'cancelled',
    'cancel inprogress',
    'request cancelled',
]);

const canCancelAdvanceRequest = (row) => {
    if (!row || TERMINAL_ADVANCE_CANCEL_STATUSES.has(row.approval_status)) return false;
    if (row.is_paid) return false;
    if (row.enable_cancel_request === false) return false;
    return row.approval_status === 'approved';
};

const AdvanceRequest = () => {
    // Constants
    const FILE_TYPES = {
        IMAGE: "image/",
        PDF: "application/pdf"
    };
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    // states
    const companyid = sessionStorage.getItem("userId");
    const { fetch, create, update } = useApiMethods()
    const [formData, setFormData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)
    const [openDialog, setOpenDialog] = useState(false)
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [wholeResponse, setWholeResponse] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showApprovalLog, setShowApprovalLog] = useState(false);
    const [dropdownData, setDropdownData] = useState({
        emiPeriods: [
            { id: 1, name: '1 Month' },
            { id: 2, name: '2 Months' },
            { id: 3, name: '3 Months' },
            { id: 4, name: '4 Months' },
            { id: 5, name: '5 Months' },
            { id: 6, name: '6 Months' },
            { id: 7, name: '7 Months' },
            { id: 8, name: '8 Months' },
            { id: 9, name: '9 Months' },
            { id: 10, name: '10 Months' },
            { id: 11, name: '11 Months' },
            { id: 12, name: '12 Months' },
        ]
    });
    const [screenLoader, setScreenLoader] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [cancelRecord, setCancelRecord] = useState(null);
    const [cancelReason, setCancelReason] = useState("");

    // Form states
    const [formState, setFormState] = useState({
        advance_requested: "",
        emi_period: "",
        reason: "",
        company_id: companyid,
        is_active: true,
        uploaded_documents: [],
        existing_attachments: [], // For editing existing attachments
        is_draft: false,
    });
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Check if existing attachments can be deleted (when editing)
    const canDeleteExisting = editRecord;

    // Combine existing attachments and new uploads for carousel display
    const allAttachments = [
        ...(formState.existing_attachments?.map(url => ({ type: 'existing', url, isExisting: true, canDelete: canDeleteExisting })) || []),
        ...(formState.uploaded_documents?.map(file => ({ type: 'new', file, isExisting: false, canDelete: true })) || [])
    ];

    const StatusBadge = ({ status }) => {
        const getStatusConfig = (status) => {
            const config = {
                pending: {
                    text: 'Pending',
                    className: 'bg-yellow-100 text-yellow-700 border-yellow-200'
                },
                approved: {
                    text: 'Approved',
                    className: 'bg-green-100 text-green-700 border-green-200'
                },
                rejected: {
                    text: 'Rejected',
                    className: 'bg-red-100 text-red-700 border-red-200'
                },
                cancelled: {
                    text: 'Cancelled',
                    className: 'bg-gray-100 text-gray-600 border-gray-200'
                }
            };

            return config[status] || {
                text: status?.charAt(0).toUpperCase() + status?.slice(1) || 'Pending',
                className: 'bg-blue-100 text-blue-700 border-blue-200'
            };
        };

        const statusConfig = getStatusConfig(status);

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.className}`}>
                {statusConfig.text}
            </span>
        );
    };

    // Time Chip Component
    const TimeChip = ({ time, type = "start" }) => {
        if (!time || time === '-') return null;

        const chipClass = type === "start"
            ? "bg-blue-100 text-blue-700 border-blue-200"
            : "bg-orange-100 text-orange-700 border-orange-200";

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${chipClass}`}>
                {formatTimeToAMPM(time)}
            </span>
        );
    };

    // Format time to AM/PM
    const formatTimeToAMPM = (timeString) => {
        if (!timeString || timeString === '-') return '';

        try {
            // Handle different time formats
            let time = timeString;
            if (timeString.includes(' ')) {
                // If it's a full datetime string, extract time part
                time = timeString.split(' ')[1];
            }

            const [hours, minutes] = time.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const formattedHour = hour % 12 || 12;

            return `${formattedHour}:${minutes} ${ampm}`;
        } catch {
            return timeString;
        }
    };

    // Format date for display
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '-';

        try {
            // Handle date strings in "DD-MM-YYYY" format
            if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
                const [day, month, year] = dateString.split('-');
                // Create date object with proper format (YYYY-MM-DD)
                const date = new Date(`${year}-${month}-${day}`);
                return date.toLocaleDateString('en-GB'); // This will show as DD/MM/YYYY
            }

            // Handle date-time strings like "02-11-2025 10:00:00 AM"
            if (dateString.includes(' ') && dateString.includes('-')) {
                const [datePart, timePart] = dateString.split(' ');
                const [day, month, year] = datePart.split('-');
                const date = new Date(`${year}-${month}-${day} ${timePart}`);
                return date.toLocaleDateString('en-GB'); // Returns DD/MM/YYYY
            }

            // For other date formats, try direct parsing
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString; // Return original if invalid
            }
            return date.toLocaleDateString('en-GB'); // Returns DD/MM/YYYY

        } catch {
            return dateString;
        }
    };

    // Check if request date has passed
    const isRequestDatePassed = (fromDate) => {
        if (!fromDate) return false;

        try {
            const requestDate = new Date(fromDate);
            const today = new Date();
            requestDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            return requestDate < today;
        } catch (error) {
            return false;
        }
    };

    // File validation and handling
    const validateFile = (file) => {
        const isImage = file.type?.startsWith(FILE_TYPES.IMAGE);
        const isPDF = file.type === FILE_TYPES.PDF;

        if (!isImage && !isPDF) {
            ToastMsg("error", "Only image and PDF files are allowed!");
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            ToastMsg("error", "File size must be less than 5MB!");
            return false;
        }

        return true;
    };

    const handleFileChange = useCallback((fileList) => {
        if (!fileList || fileList.length === 0) {
            setFormState(prev => ({ ...prev, uploaded_documents: [] }));
            setCurrentImageIndex(0);
            return;
        }

        const validFiles = fileList.filter(file => validateFile(file));

        setFormState(prev => {
            const existingFiles = prev.uploaded_documents || [];
            // Merge new files with existing, avoiding duplicates by uid
            const mergedFiles = [...existingFiles];
            validFiles.forEach(newFile => {
                const exists = mergedFiles.some(existing => existing.uid === newFile.uid);
                if (!exists) {
                    mergedFiles.push(newFile);
                }
            });
            return {
                ...prev,
                uploaded_documents: mergedFiles
            };
        });
        setCurrentImageIndex(0);
    }, []);

    // Handle form field changes
    const handleFormChange = (field, value) => {
        setFormState(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // columns
    const columns = [
        {
            Header: 'Date',
            accessor: 'date',
            filterable: true,
            sortable: true,
            width: '150px',
            filterType: 'date',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {formatDateDisplay(value)}
                </span>
            )
        },
        {
            Header: 'Advance Requested Amount',
            accessor: 'advance_requested',
            sortable: true,
            filterable: true,
            width: '200px',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                    ₹{value || 0}
                </span>
            )
        },
        {
            Header: 'Debit From',
            accessor: 'debit_from',
            sortable: true,
            filterable: true,
            filterType: 'date',
            width: '150px',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300">
                    {value || '-'}
                </span>
            )
        },
        {
            Header: 'EMI Period',
            accessor: 'emi_period',
            sortable: true,
            filterable: true,
            width: '120px',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300">
                    {value ? `${value} Months` : '-'}
                </span>
            )
        },
        {
            Header: 'Advance Approved',
            accessor: 'advance_approved',
            sortable: true,
            filterable: true,
            width: '150px',
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                    ₹{value || 0}
                </span>
            )
        },
        {
            Header: 'Status',
            accessor: 'approval_status',
            sortable: true,
            filterable: true,
            filterType: 'requestStatus',
            Cell: ({ value }) => <StatusBadge status={value} />,
            width: '120px'
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            sortable: false,
            fixed: true,
            align: 'right',
            width: '80px',
            Cell: ({ row }) => {
                const isDraftRow = Boolean(row.is_draft);
                const isPending = row.approval_status === 'pending';
                const canEditOrDelete = isDraftRow || isPending;

                return (
                    <div className="flex justify-end gap-2">
                        {canEditOrDelete ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setIsEdit(true);
                                        setEditRecord(row);
                                        setFormState({
                                            advance_requested: row.advance_requested || '',
                                            emi_period: row.emi_period || '',
                                            reason: row.reason || '',
                                            uploaded_documents: [], // New files being uploaded
                                            existing_attachments: row.approval_status !== 'approved' ? (row.uploaded_documents || []) : [], // Existing attachments if not approved
                                            company_id: parseInt(companyid),
                                            is_active: true,
                                            request_type: "advance request",
                                            is_draft: Boolean(row.is_draft),
                                        });
                                        setCurrentImageIndex(0);
                                        setOpenDialog(true);
                                    }}
                                    className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700"
                                    title={isDraftRow ? "Edit Draft" : "Edit Request"}
                                >
                                    <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setDeleteRecord(row);
                                        setShowDeleteConfirm(true);
                                    }}
                                    className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700"
                                    title="Delete Request"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setSelectedRequest(row);
                                        setShowApprovalLog(true);
                                    }}
                                    disabled={!row.approval_log || row.approval_log.length === 0}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${!row.approval_log || row.approval_log.length === 0
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:text-amber-700'
                                        }`}
                                    title={(!row.approval_log || row.approval_log.length === 0) ? "No approval log available" : "View Approval Log"}
                                >
                                    <History className="h-3.5 w-3.5" />
                                </Button>
                                {canCancelAdvanceRequest(row) && (
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setCancelRecord(row);
                                        setCancelReason("");
                                        setShowCancelDialog(true);
                                    }}
                                    className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 hover:text-orange-700"
                                    title="Cancel Request"
                                >
                                    <Rewind className="h-3.5 w-3.5" />
                                </Button>
                                )}
                            </>
                        )}
                    </div>
                )
            }
        }
    ];

    // Handle delete with confirmation
    const handleDeleteRequest = async () => {
        if (!deleteRecord) return;

        setLoading(true);
        try {
            const response = await update(ADVANCE_URL, {
                id: deleteRecord.id,
                is_active: false
            });

            if (response.status) {
                ToastMsg('success', 'Advance request deleted successfully');
                setShowDeleteConfirm(false);
                setDeleteRecord(null);
                setServerTrigger(!serverTrigger); // Refresh the table
            } else {
                ToastMsg('error', response.message || 'Failed to delete request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while deleting the request');
            console.error('Delete error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel request
    const handleCancelRequest = async () => {
        if (!cancelRecord || !cancelReason.trim()) {
            ToastMsg('error', 'Please provide a cancel reason');
            return;
        }

        setLoading(true);
        try {
            const response = await update(ADVANCE_URL, {
                ...cancelRecord,
                cancel_reason: cancelReason,
                approval_status: 'cancel inprogress',
                request_type: "cancel request"
            });

            if (response.status) {
                ToastMsg('success', 'Request cancelled successfully');
                setShowCancelDialog(false);
                setCancelRecord(null);
                setCancelReason("");
                setServerTrigger(!serverTrigger);
            } else {
                ToastMsg('error', response.message || 'Failed to cancel request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while cancelling the request');
            console.error('Cancel error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmitRequest = async (isDraft = false) => {
        if (!isDraft) {
            if (!formState.advance_requested || Number(formState.advance_requested) <= 0) {
                ToastMsg('error', 'Advance amount must be greater than 0');
                return;
            }
            if (!formState.emi_period || !formState.reason) {
                ToastMsg('error', 'Please fill in all required fields');
                return;
            }
        } else if (!formState.advance_requested && !formState.reason) {
            ToastMsg('error', 'Enter at least an amount or reason before saving a draft');
            return;
        }

        setLoading(true);

        try {
            const dataForm = new FormData();
            dataForm.append("advance_requested", formState.advance_requested ? +formState.advance_requested : 0)
            dataForm.append("emi_period", formState.emi_period || "")
            dataForm.append("reason", formState.reason || "")
            if (formState.uploaded_documents?.length > 0) {
                formState.uploaded_documents.forEach((file, index) => {
                    dataForm.append(`uploaded_documents_${index + 1}`, file.originFileObj || file);
                });
            }
            dataForm.append("is_active", formState.is_active || true)
            dataForm.append("company_id", formState.company_id || "")
            dataForm.append("request_type", formState.request_type || "advance request")
            dataForm.append("is_draft", isDraft ? "true" : "false");

            // Append existing attachments (URLs) first
            if (formState.existing_attachments?.length > 0) {
                formState.existing_attachments.forEach((url, index) => {
                    dataForm.append(`uploaded_documents_${index + 1}`, url);
                });
            }

            // Append new uploaded files
            if (formState.uploaded_documents?.length > 0) {
                const startIndex = formState.existing_attachments?.length || 0;
                formState.uploaded_documents.forEach((file, index) => {
                    dataForm.append(`uploaded_documents_${startIndex + index + 1}`, file.originFileObj || file);
                });
            }

            const isUpdate = isEdit && editRecord?.id;
            if (isUpdate) {
                dataForm.append("id", editRecord.id);
            }

            const response = isUpdate
                ? await update(ADVANCE_URL, dataForm, true)
                : await create(ADVANCE_URL, dataForm, true);

            if (response.status) {
                ToastMsg(
                    'success',
                    isDraft
                        ? (response.message || 'Draft saved successfully')
                        : (isEdit ? 'Advance request updated successfully' : 'Advance request submitted successfully')
                );
                setOpenDialog(false);
                setIsEdit(false);
                setEditRecord(null);
                // Reset form state
                setFormState({
                    advance_requested: "",
                    emi_period: "",
                    reason: "",
                    uploaded_documents: [],
                    company_id: companyid,
                    is_active: true,
                    request_type: "advance request",
                    is_draft: false,
                });
                setCurrentImageIndex(0);
                setServerTrigger(!serverTrigger); // Refresh the table
            } else {
                ToastMsg('error', response.message || 'Failed to submit request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while submitting the request');
            console.error('Submission error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Reset form function
    const resetForm = () => {
        setIsEdit(false);
        setEditRecord(null);
        setFormState({
            advance_requested: "",
            emi_period: "",
            reason: "",
            uploaded_documents: [],
            existing_attachments: [],
            company_id: companyid,
            is_active: true,
            request_type: "advance request",
            is_draft: false,
        });
        setCurrentImageIndex(0);
    };

    const statusStyles = {
        pending: {
            bg: "bg-amber-50 dark:bg-amber-950/20",
            border: "border-amber-200",
            text: "text-amber-700 dark:text-amber-400",
        },
        approved: {
            bg: "bg-emerald-50 dark:bg-emerald-950/20",
            border: "border-emerald-200",
            text: "text-emerald-700 dark:text-emerald-400",
        },
        rejected: {
            bg: "bg-red-50 dark:bg-red-950/20",
            border: "border-red-200",
            text: "text-red-700 dark:text-red-400",
        },
        cancelled: {
            bg: "bg-slate-50 dark:bg-slate-900/20",
            border: "border-slate-200",
            text: "text-slate-700 dark:text-slate-400",
        },
        repaying: {
            bg: "bg-blue-50 dark:bg-blue-950/20",
            border: "border-blue-200",
            text: "text-blue-700 dark:text-blue-400",
        },
        all: {
            bg: "bg-violet-50 dark:bg-violet-950/20",
            border: "border-violet-200",
            text: "text-violet-700 dark:text-violet-400",
        },
    };

    const renderAdvanceDetailCards = (record) => {
        if (!record) return null;

        const detailCards = [
            {
                label: "Requested",
                value: `₹${(record.advance_requested || 0).toLocaleString()}`,
                style: statusStyles.pending,
            },
            {
                label: "Approved",
                value: `₹${(record.advance_approved || record.approval_amount || 0).toLocaleString()}`,
                style: statusStyles.approved,
            },
            {
                label: "EMI",
                value: record.emi_period ? `${record.emi_period} mo · ₹${(record.emi_amount || 0).toLocaleString()}/mo` : "-",
                style: statusStyles.repaying,
            },
            {
                label: "Repayment",
                value: record.is_paid
                    ? "Fully repaid"
                    : `${record.installments_paid || 0}/${record.emi_period || 0} paid · ₹${(record.outstanding_balance || 0).toLocaleString()} due`,
                style: record.is_paid ? statusStyles.approved : statusStyles.repaying,
            },
        ];

        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {detailCards.map((card) => (
                    <div
                        key={card.label}
                        className={`${card.style.bg} border ${card.style.border} rounded-xl p-3 shadow-sm`}
                    >
                        <p className={`text-xs font-medium uppercase tracking-wide ${card.style.text}`}>
                            {card.label}
                        </p>
                        <p className={`text-sm font-semibold mt-1 ${card.style.text}`}>
                            {card.value}
                        </p>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <ContainerCard>
            {/* title */}
            <ModuleTitleCard
                Icon={HandCoins}
                title={'Advance Salary Request'}
                subtitle={'Request advance salary with EMI options'}
                totalRecords={totalRecords}
                buttonEnable
                setOpenDialog={setOpenDialog}
                buttonName={'Add Advance Request'}
            />

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-2 mb-2">
                {Object.entries(wholeResponse?.data?.summary || {}).map(([key, value]) => {
                    const style = statusStyles[key];
                    if (!style) return null;

                    return (
                        <div
                            key={key}
                            className={`${style.bg} dark:bg-slate-900 border ${style.border} rounded-xl p-2.5 shadow-sm hover:shadow-md transition-all`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h4 className={`text-xs font-medium uppercase tracking-wide ${style.text}`}>
                                    {key}
                                </h4>
                                <span className={`text-lg font-bold ${style.text}`}>
                                    {value.count}
                                </span>
                            </div>

                            <div className="border-t pt-2">
                                {key === "all" ? (
                                    <>
                                        <div className="flex justify-between text-xs">
                                            <span>Requested</span>
                                            <span className="font-semibold">
                                                ₹{value.requested_amount?.toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-xs mt-1 text-green-600">
                                            <span>Approved</span>
                                            <span className="font-semibold">
                                                ₹{value.approved_amount?.toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-xs mt-1 text-blue-600">
                                            <span>Outstanding</span>
                                            <span className="font-semibold">
                                                ₹{value.outstanding_amount?.toLocaleString()}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex justify-between text-xs">
                                        <span>Amount</span>
                                        <span className={`font-semibold ${style.text}`}>
                                            ₹{value.amount?.toLocaleString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Data Table */}
            <Reactable
                columns={columns}
                data={formData}
                setData={setFormData}
                setTotalCount={setTotalRecords}
                serverSide
                url={ADVANCE_URL}
                serverTrigger={serverTrigger}
                setWholeResponse={setWholeResponse}
                rowSize={"md"}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={true}
            />

            {/* Advance Request Details Dialog */}
            <DialogModal
                open={showApprovalLog}
                onClose={() => setShowApprovalLog(false)}
                title="Advance Request Details"
                showActions={false}
                width="900px"
            >
                {selectedRequest ? (
                    <div className="space-y-4">
                        {renderAdvanceDetailCards(selectedRequest)}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                            <p><strong>Request Date:</strong> {formatDateDisplay(selectedRequest.date)}</p>
                            <p><strong>Debit From:</strong> {formatDateDisplay(selectedRequest.debit_from) || "-"}</p>
                            <p><strong>Current Status:</strong> <StatusBadge status={selectedRequest.approval_status} /></p>
                            <p><strong>Repayment Status:</strong> {selectedRequest.is_paid ? "Fully repaid" : "In progress"}</p>
                            <p className="md:col-span-2"><strong>Reason:</strong> {selectedRequest.reason || "-"}</p>
                        </div>

                        {Array.isArray(selectedRequest.repayment_history) && selectedRequest.repayment_history.length > 0 && (
                            <>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Instalment history</h4>
                                <div className="overflow-x-auto relative">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-800">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Month</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            {selectedRequest.repayment_history.map((row, index) => (
                                                <tr key={`${row.entry_type}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {row.payroll_month && row.payroll_year
                                                            ? dayjs(`${row.payroll_year}-${String(row.payroll_month).padStart(2, '0')}-01`).format('MMM YYYY')
                                                            : '-'}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 capitalize">{(row.entry_type || '').replace('_', ' ') || '-'}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">₹{(row.amount || 0).toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 capitalize">{row.status || '-'}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{row.skip_reason || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {selectedRequest.approval_log && selectedRequest.approval_log.length > 0 ? (
                            <>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Approval Log</h4>
                                <div className="overflow-x-auto h-60 relative">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky top-0 bg-gray-50 dark:bg-gray-800">
                                                    Date & Time
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky top-0 bg-gray-50 dark:bg-gray-800">
                                                    Approver
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky top-0 bg-gray-50 dark:bg-gray-800">
                                                    Status
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky top-0 bg-gray-50 dark:bg-gray-800">
                                                    Notes
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            {selectedRequest.approval_log.map((log, index) => (
                                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                        {new Date(log.approved_date_and_time).toLocaleString()}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                        {log.approver_name || '-'}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <StatusBadge status={log.approval_status} />
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {log.approval_notes || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-gray-500">No approval log available for this request.</p>
                            </div>
                        )}

                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            <div>
                                {(() => {
                                    const record = selectedRequest;
                                    let empDetails = {
                                        ...record?.employee,
                                        designation_id: record?.employee?.designation
                                            ? {
                                                id: record?.employee.designation.id,
                                                designation_name: record?.employee.designation.name,
                                            }
                                            : { id: 0, designation_name: "" },
                                        first_name: record?.employee?.employee_name || "-",
                                        id: record?.employee?.employee_id || "-",
                                    };
                                    return <EmployeeDetails details={empDetails} size="xs" />;
                                })()}
                            </div>
                            <p><strong>Request Type:</strong> Advance Salary</p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No request details available.</p>
                    </div>
                )}
            </DialogModal>

            {/* Delete Confirmation Dialog */}
            <DialogModal
                open={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDeleteRecord(null);
                }}
                onSave={handleDeleteRequest}
                title="Delete Advance Request"
                saveLabel="Delete"
                width="400px"
                loading={loading}
                saveButtonColor="destructive"
            >
                <div className="text-center py-4">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                        <Trash2 className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Delete Advance Request
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this advance request? This action cannot be undone.
                    </p>
                    {deleteRecord && (
                        <div className="mt-4 p-3 flex flex-col gap-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Amount:</strong> ₹{deleteRecord.advance_requested || 0}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>EMI Period:</strong> {deleteRecord.emi_period ? `${deleteRecord.emi_period} Months` : 'N/A'}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Status:</strong> <StatusBadge status={deleteRecord.approval_status} />
                            </p>
                        </div>
                    )}
                </div>
            </DialogModal>

            {/* Add Advance Request Dialog */}
            <DialogModal
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    resetForm();
                }}
                onSave={() => handleSubmitRequest(false)}
                onDraft={(isEdit && !editRecord?.is_draft) ? undefined : () => handleSubmitRequest(true)}
                title={isEdit ? (editRecord?.is_draft ? "Edit Draft Request" : "Edit Advance Request") : "Apply Advance Request"}
                saveLabel={isEdit && !editRecord?.is_draft ? "Update Request" : "Submit Request"}
                draftLabel="Save Draft"
                width="700px"
                loading={loading}
            >
                <div className={cn("grid gap-8 grid-cols-1 md:grid-cols-2 p-2", "max-h-90 overflow-y-auto pr-2")}>

                    {/* Left Column */}
                    <div className="space-y-4">
                        {/* Amount */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Amount <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formState.advance_requested}
                                onChange={(e) => handleFormChange('advance_requested', e.target.value)}
                                placeholder="Enter advance amount"
                                className="w-full"
                                type="number"
                                min={0}
                            />
                        </div>

                        {/* EMI Period */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                EMI Period <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={formState.emi_period}
                                onChange={(value) => handleFormChange('emi_period', value)}
                                placeholder="Select EMI period"
                                className="w-full"
                                size="large"
                            >
                                {dropdownData.emiPeriods.map((emi) => (
                                    <Select.Option key={emi.id} value={emi.id}>
                                        {emi.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>

                        {/* Reason */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <Textarea
                                value={formState.reason}
                                onChange={(e) => handleFormChange('reason', e.target.value)}
                                placeholder="Please provide a reason for your advance request..."
                                rows={5}
                                showCharCount={true}
                                maxLength={500}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">

                        {/* File Upload */}
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Attachment
                            </label>
                            <div>
                                <AntUpload.Dragger
                                    accept="image/*,.pdf"
                                    multiple
                                    maxCount={10}
                                    beforeUpload={() => false}
                                    onChange={({ fileList }) => handleFileChange(fileList)}
                                    showUploadList={false}
                                    className="dark:bg-gray-800 dark:border-gray-700"
                                >
                                    <UploadCloudIcon className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                        Click to upload document
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        Supported formats: .jpg, .png, .pdf (Max: 5MB)
                                    </p>
                                </AntUpload.Dragger>

                                {/* File Preview */}
                                {allAttachments && allAttachments.length > 0 && (
                                    <div className="mt-2">
                                        {allAttachments.length === 1 ? (
                                            // SINGLE FILE PREVIEW
                                            <>
                                                {allAttachments[0].isExisting ? (
                                                    // Existing attachment (URL)
                                                    <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                        {allAttachments[0].url?.toLowerCase().includes('.pdf') ? (
                                                            <a
                                                                href={allAttachments[0].url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-sidebar underline underline-offset-2 text-sm flex items-center gap-2"
                                                            >
                                                                Preview PDF <Eye className="w-3 h-3" />
                                                            </a>
                                                        ) : (
                                                            <Image
                                                                src={allAttachments[0].url}
                                                                alt="existing attachment"
                                                                width={120}
                                                                className="rounded-md shadow-sm"
                                                            />
                                                        )}
                                                        <span className="text-xs text-green-600 dark:text-green-400">Existing</span>
                                                        {allAttachments[0].canDelete && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => {
                                                                    const currentUrl = allAttachments[0].url;
                                                                    const updatedExisting = formState.existing_attachments.filter(url => url !== currentUrl);
                                                                    handleFormChange('existing_attachments', updatedExisting);
                                                                }}
                                                                className="text-red-600 p-2"
                                                                title="Remove existing file"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    // New uploaded file - Individual delete
                                                    <>
                                                        {allAttachments[0].file.type?.startsWith("image/") ? (
                                                            <div className="relative p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                                <Image
                                                                    src={URL.createObjectURL(allAttachments[0].file.originFileObj || allAttachments[0].file)}
                                                                    alt="preview"
                                                                    width={120}
                                                                    className="rounded-md shadow-sm"
                                                                />
                                                                <button
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-sm"
                                                                    title="Remove file"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ) : allAttachments[0].file.type === "application/pdf" ? (
                                                            <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                                <a
                                                                    href={URL.createObjectURL(allAttachments[0].file.originFileObj || allAttachments[0].file)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-sidebar underline underline-offset-2 text-sm flex items-center gap-2"
                                                                >
                                                                    Preview PDF <Eye className="w-3 h-3" />
                                                                </a>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="text-red-600 p-2"
                                                                    title="Remove file"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                                                    {allAttachments[0].file.name}
                                                                </span>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="text-red-600 p-2"
                                                                    title="Remove file"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            // MULTIPLE FILES CAROUSEL
                                            <div className="space-y-4">
                                                {/* Main Preview */}
                                                <div className="relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                                    <div className="flex justify-center items-center min-h-[150px] p-6">
                                                        {allAttachments[currentImageIndex].isExisting ? (
                                                            // Existing attachment (URL)
                                                            <>
                                                                {allAttachments[currentImageIndex].url?.toLowerCase().includes('.pdf') ? (
                                                                    <div className="text-center max-h-[100px]">
                                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">PDF Document</p>
                                                                        <a
                                                                            href={allAttachments[currentImageIndex].url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-700 text-white text-sm rounded-md"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                            View PDF
                                                                        </a>
                                                                    </div>
                                                                ) : (
                                                                    <Image
                                                                        src={allAttachments[currentImageIndex].url}
                                                                        alt={`existing ${currentImageIndex + 1}`}
                                                                        className="max-w-full max-h-[100px] object-contain rounded"
                                                                    />
                                                                )}
                                                            </>
                                                        ) : (
                                                            // New uploaded file
                                                            <>
                                                                {allAttachments[currentImageIndex].file.type?.startsWith("image/") ? (
                                                                    <Image
                                                                        src={URL.createObjectURL(allAttachments[currentImageIndex].file.originFileObj || allAttachments[currentImageIndex].file)}
                                                                        alt={`preview ${currentImageIndex + 1}`}
                                                                        className="max-w-full max-h-[100px] object-contain rounded"
                                                                    />
                                                                ) : allAttachments[currentImageIndex].file.type === "application/pdf" ? (
                                                                    <div className="text-center max-h-[100px]">
                                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">PDF Document</p>
                                                                        <a
                                                                            href={URL.createObjectURL(allAttachments[currentImageIndex].file.originFileObj || allAttachments[currentImageIndex].file)}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-700 text-white text-sm rounded-md"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                            View PDF
                                                                        </a>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center">
                                                                        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                                                                            <UploadCloudIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                                                                        </div>
                                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                            {allAttachments[currentImageIndex].file.name}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Navigation Arrows */}
                                                    <button
                                                        onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : allAttachments.length - 1)}
                                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full flex items-center justify-center"
                                                    >
                                                        <ChevronLeft className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setCurrentImageIndex(prev => prev < allAttachments.length - 1 ? prev + 1 : 0)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full flex items-center justify-center"
                                                    >
                                                        <ChevronRight className="w-4 h-4" />
                                                    </button>

                                                    {/* File Counter */}
                                                    <div className="absolute top-2 right-2 bg-gray-900 text-white px-2 py-1 rounded text-xs">
                                                        {currentImageIndex + 1} / {allAttachments.length}
                                                    </div>

                                                    {/* Existing File Badge */}
                                                    {allAttachments[currentImageIndex].isExisting && (
                                                        <div className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs">
                                                            Existing
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Thumbnails */}
                                                <div className="flex gap-2 overflow-x-auto pb-2">
                                                    {allAttachments.map((attachment, index) => (
                                                        <div key={attachment.isExisting ? attachment.url : attachment.file.uid || index} className="relative flex-shrink-0 group">
                                                            <button
                                                                onClick={() => setCurrentImageIndex(index)}
                                                                className={`relative w-12 h-12 rounded border-2 overflow-hidden ${index === currentImageIndex
                                                                    ? 'border-blue-500'
                                                                    : 'border-gray-300 dark:border-gray-600'
                                                                    }`}
                                                            >
                                                                {attachment.isExisting ? (
                                                                    // Existing attachment thumbnail
                                                                    <>
                                                                        {attachment.url?.toLowerCase().includes('.pdf') ? (
                                                                            <div className="w-full h-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                                                                <span className="text-xs text-red-600 dark:text-red-400">PDF</span>
                                                                            </div>
                                                                        ) : (
                                                                            <Image
                                                                                src={attachment.url}
                                                                                alt={`existing thumb ${index + 1}`}
                                                                                className="w-full h-full object-cover"
                                                                            />
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    // New file thumbnail
                                                                    <>
                                                                        {attachment.file.type?.startsWith("image/") ? (
                                                                            <Image
                                                                                src={URL.createObjectURL(attachment.file.originFileObj || attachment.file)}
                                                                                alt={`thumb ${index + 1}`}
                                                                                className="w-full h-full object-cover"
                                                                            />
                                                                        ) : attachment.file.type === "application/pdf" ? (
                                                                            <div className="w-full h-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                                                                <span className="text-xs text-red-600 dark:text-red-400">PDF</span>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                                                                <span className="text-xs text-gray-600 dark:text-gray-400">FILE</span>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {/* Existing file indicator */}
                                                                {attachment.isExisting && (
                                                                    <div className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border border-white"></div>
                                                                )}
                                                            </button>

                                                            {/* Individual delete button for deletable files */}
                                                            {attachment.canDelete && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (attachment.isExisting) {
                                                                            // Remove from existing attachments
                                                                            const currentUrl = attachment.url;
                                                                            const updatedExisting = formState.existing_attachments.filter(url => url !== currentUrl);
                                                                            handleFormChange('existing_attachments', updatedExisting);
                                                                        } else {
                                                                            // Remove from new uploads
                                                                            const currentFile = attachment.file;
                                                                            const updatedFiles = formState.uploaded_documents.filter(file =>
                                                                                file.uid !== currentFile.uid
                                                                            );
                                                                            handleFormChange('uploaded_documents', updatedFiles);
                                                                        }
                                                                        // Adjust current index if necessary
                                                                        const newTotalAttachments = (attachment.isExisting ?
                                                                            formState.existing_attachments.length - 1 :
                                                                            formState.existing_attachments.length) +
                                                                            (attachment.isExisting ?
                                                                                formState.uploaded_documents.length :
                                                                                formState.uploaded_documents.length - 1);
                                                                        if (index === currentImageIndex && index >= newTotalAttachments) {
                                                                            setCurrentImageIndex(Math.max(0, index - 1));
                                                                        } else if (index < currentImageIndex) {
                                                                            setCurrentImageIndex(currentImageIndex - 1);
                                                                        }
                                                                    }}
                                                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    title="Remove this file"
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* File Info and Actions */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                                            {allAttachments[currentImageIndex].isExisting
                                                                ? 'Existing file'
                                                                : truncateText(allAttachments[currentImageIndex].file.name, 20, "200px", "balance")
                                                            }
                                                        </span>
                                                        {allAttachments[currentImageIndex].isExisting && !allAttachments[currentImageIndex].canDelete && (
                                                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                                                                Cannot remove existing files
                                                            </span>
                                                        )}
                                                    </div>
                                                    {allAttachments[currentImageIndex].canDelete && (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => {
                                                                    if (allAttachments[currentImageIndex].isExisting) {
                                                                        // Remove from existing attachments
                                                                        const currentUrl = allAttachments[currentImageIndex].url;
                                                                        const updatedExisting = formState.existing_attachments.filter(url => url !== currentUrl);
                                                                        handleFormChange('existing_attachments', updatedExisting);
                                                                    } else {
                                                                        // Remove from new uploads
                                                                        const currentFile = allAttachments[currentImageIndex].file;
                                                                        const updatedFiles = formState.uploaded_documents.filter(file =>
                                                                            file.uid !== currentFile.uid
                                                                        );
                                                                        handleFormChange('uploaded_documents', updatedFiles);
                                                                    }
                                                                    // Adjust current index if necessary
                                                                    const newTotalAttachments = (allAttachments[currentImageIndex].isExisting ?
                                                                        formState.existing_attachments.length - 1 :
                                                                        formState.existing_attachments.length) +
                                                                        (allAttachments[currentImageIndex].isExisting ?
                                                                            formState.uploaded_documents.length :
                                                                            formState.uploaded_documents.length - 1);
                                                                    if (currentImageIndex >= newTotalAttachments && currentImageIndex > 0) {
                                                                        setCurrentImageIndex(currentImageIndex - 1);
                                                                    }
                                                                }}
                                                                className="text-red-600 p-2"
                                                                title="Remove this file"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                            {formState.uploaded_documents.length > 1 && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                                    className="text-red-600 p-2"
                                                                    title="Remove all new files"
                                                                >
                                                                    <div className="relative">
                                                                        <Trash2 className="w-4 h-4" />
                                                                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full flex items-center justify-center">
                                                                            <span className="text-xs text-white font-bold">+</span>
                                                                        </div>
                                                                    </div>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </DialogModal>

            {/* Cancel Request Dialog */}
            <DialogModal
                open={showCancelDialog}
                onClose={() => {
                    setShowCancelDialog(false);
                    setCancelRecord(null);
                    setCancelReason("");
                }}
                onSave={handleCancelRequest}
                title="Cancel Advance Request"
                saveLabel="Cancel Request"
                width="500px"
                loading={loading}
                saveButtonColor="destructive"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Please provide a reason for cancelling this advance request.
                    </p>
                    <Textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Enter cancel reason..."
                        rows={4}
                        showCharCount={true}
                        maxLength={500}
                        className="w-full"
                    />
                    {cancelRecord && (
                        <div className="p-3 flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-md">
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>Amount:</strong> ₹{cancelRecord.advance_requested || 0}
                            </p>
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>EMI Period:</strong> {cancelRecord.emi_period ? `${cancelRecord.emi_period} Months` : 'N/A'}
                            </p>
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>Status:</strong> <StatusBadge status={cancelRecord.approval_status} />
                            </p>
                        </div>
                    )}
                </div>
            </DialogModal>

        </ContainerCard>
    );
}

export default AdvanceRequest;